<template>
  <Dropdown
    ref="dropdownRef"
    v-model="selectedValue"
    :options="options"
    :option-label="typeof options[0] === 'object' ? optionLabel : undefined"
    :option-value="typeof options[0] === 'object' ? optionValue : undefined"
    :placeholder="placeholder"
    :show-clear="clearable"
    :append-to="autoAppendTarget"
    :class="{
      'h-9': size === 'sm',
    }"
  >
    <template #value>
      <span class="flex items-center gap-2 h-full">
        <AppIcon
          v-if="icon"
          :icon="icon"
          class="shrink-0 text-base"
        />
        <span
          class="grow truncate h-full flex flex-col justify-center"
          :class="{
            'text-surface-800':
              selectedValue !== null && selectedValue !== undefined,
            'text-surface-500':
              selectedValue === null || selectedValue === undefined,
          }"
          >{{ selectedLabelOrPlaceholder }}</span
        >
      </span>
    </template>
    <!-- <template #header>
      <div class="flex items-center gap-2 px-3 py-2 bg-surface-100">
        <AppInputText
          v-model="searchText"
          size="sm"
          placeholder="Search"
          prepend-icon="fa-regular fa-magnifying-glass"
          class="w-full"
        />
      </div>
    </template> -->
    <template #option="{ option }">
      <slot
        name="option"
        :option="option"
      />
      <span
        v-if="!$slots.option"
        class="flex items-center gap-2"
      >
        <!-- <AppIndicatorDot
          :class="option.color ?? 'text-current'"
          class="bg-current"
        /> -->
        <AppIcon
          v-if="option.icon"
          :icon="option.icon"
          class="shrink-0 text-base"
        />
        <span class="grow">{{
          typeof option === 'string'
            ? option
            : (option[optionLabel] ?? '[Unknown Option]')
        }}</span>
        <template v-if="useCheckmark">
          <AppIcon
            v-if="
              (Object.hasOwn(option, 'value') &&
                option.value === selectedValue) ||
              option === selectedValue
            "
            icon="fa-regular fa-check"
            class="shrink-0 text-base"
          />
        </template>
      </span>
    </template>

    <template #loadingicon>
      <AppLoadingSpinner class="size-5" />
    </template>
  </Dropdown>
</template>

<script setup lang="ts">
import type { ComponentPublicInstance } from 'vue'
import Dropdown from 'primevue/dropdown'

const props = defineProps({
  modelValue: {
    type: [String, Number, Boolean] as PropType<
      string | number | boolean | null
    >,
    required: false,
    default: null,
  },
  options: {
    type: Array as PropType<
      | string[]
      | number[]
      | { value: string | number | null | undefined; label: string }[]
    >,
    required: true,
    default: () => [],
  },
  optionLabel: {
    type: String,
    required: false,
    default: 'label',
  },
  optionValue: {
    type: String,
    required: false,
    default: 'value',
  },
  placeholder: {
    type: String,
    required: false,
    default: null,
  },
  clearable: {
    type: Boolean,
    required: false,
    default: false,
  },
  useIndicatorDots: {
    type: Boolean,
    required: false,
    default: false,
  },
  icon: {
    type: String,
    required: false,
    default: undefined,
  },
  size: {
    type: String as PropType<'sm' | 'md'>,
    required: false,
    default: 'md',
  },
  useCheckmark: {
    type: Boolean,
    required: false,
    default: false,
  },
})

const emit = defineEmits(['update:modelValue'])

const dropdownRef = ref<ComponentPublicInstance | null>(null)
const autoAppendTarget = useOverlayAppendTarget(dropdownRef)

const selectedValue = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value),
})

const selectedLabelOrPlaceholder = computed(() => {
  if (selectedValue.value === null) return props.placeholder

  if (props.options.length === 0) return props.placeholder

  if (typeof props.options[0] !== 'object') {
    return selectedValue.value
  }

  const selectedOption = props.options.find((option: any) => {
    if (typeof option === 'object' && props.optionValue in option) {
      return option[props.optionValue] === selectedValue.value
    }

    return false
  })

  if (!selectedOption) return props.placeholder

  if (typeof selectedOption === 'object') {
    return selectedOption[props.optionLabel as keyof typeof selectedOption]
  }

  return null
})
</script>
